import { MediaElement, SlotFrame } from 'editor/src/store/design/types';
import { buildAdvPersoPluginsUpdate, getAdvPersoDataFromElement } from 'editor/src/store/plugins/utils/advPersoUtils';

import { slotProductId, slotTemplateSharedFieldId } from './slotLayoutIds';
import { getSlotTransform, transformSlotGeometry } from './transformSlotElement';

export interface SlotConditionMap {
  from: string;
  to: string;
  /** Template product's option id → target product's, paired by position. */
  optionIds: Map<string, string>;
}

export interface SlotCloneOptions {
  uuid: number;
  layoutId: string;
  template: SlotFrame;
  target: SlotFrame;
  conditions?: SlotConditionMap;
  /** The layer's shared value (or link group) is one for every product: the copy keeps the id as is. */
  keepSharedId?: boolean;
}

/**
 * A template layer as it appears in another frame. Shared values and linked pictures follow the
 * product: copies for the template's own product keep its ids, copies for another product get
 * that product's own, so every frame of one product holds one price and one machine.
 */
function cloneSlotElement(element: MediaElement, options: SlotCloneOptions): MediaElement {
  const { uuid, layoutId, template, target, conditions, keepSharedId } = options;
  const sameProduct = template.product === target.product;
  const clone = {
    ...element,
    ...transformSlotGeometry(element, getSlotTransform(template, target)),
    uuid,
    hidden: undefined,
    slotFrame: { layoutId, frameId: target.id, sourceUuid: element.uuid },
  } as MediaElement;

  if (clone.type === 'text') {
    const extra = { ...clone.extra };
    if (extra.sharedFieldId && !sameProduct && !keepSharedId) {
      extra.sharedFieldId = slotProductId(extra.sharedFieldId, target.product);
    }
    if (extra.textVariants && conditions && extra.textVariants.conditionId === conditions.from) {
      const values: typeof extra.textVariants.values = {};
      Object.entries(extra.textVariants.values ?? {}).forEach(([optionId, variant]) => {
        const mapped = conditions.optionIds.get(optionId);
        if (mapped) {
          values[mapped] = variant;
        }
      });
      extra.textVariants = { ...extra.textVariants, conditionId: conditions.to, values };
    }
    clone.extra = extra;
  }

  if (clone.type === 'image') {
    const data = getAdvPersoDataFromElement(clone);
    if (data && (!data.linkGroupId || (!sameProduct && !keepSharedId))) {
      clone.plugins = buildAdvPersoPluginsUpdate(clone, {
        ...data,
        linkGroupId: slotProductId(
          data.linkGroupId ?? slotTemplateSharedFieldId(layoutId, element.uuid),
          target.product,
        ),
      });
    }
  }

  return clone;
}

export default cloneSlotElement;
